import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';

import {
  downloadBusinessDashboardAncPatients,
  getBusinessDashboard,
  getBusinessDashboardAncPatients,
  type BusinessDashboardAncPatientsResponse,
  type BusinessDashboardPeriod,
  type BusinessDashboardQuery,
  type BusinessDashboardResponse,
} from '../lib/api/business-dashboard';
import { loadChartConstructor } from '../lib/third-party/chart';

type BusinessChartInstance = { destroy: () => void };
type BusinessChartConstructor = new (context: HTMLCanvasElement, config: Record<string, unknown>) => BusinessChartInstance;

type BusinessKpiTone = 'purple' | 'cyan' | 'pink' | 'green' | 'blue' | 'violet' | 'orange' | 'amber';

type BusinessKpi = {
  label: string;
  value: string;
  tone: BusinessKpiTone;
  icon: ReactNode;
  trend?: string;
  trendDirection?: 'up' | 'down';
  comparison?: string;
  detail?: ReactNode;
  progress?: number;
  progressLabel?: string;
  badge?: string;
  note?: ReactNode;
};

const KPI_TONES: Record<BusinessKpiTone, string> = {
  purple: '#7c3aed',
  cyan: '#0ea5e9',
  pink: '#e91e63',
  green: '#22c55e',
  blue: '#2563eb',
  violet: '#8b5cf6',
  orange: '#f97316',
  amber: '#f59e0b',
};

function numberText(value: number | null | undefined) {
  return value == null ? '—' : value.toLocaleString('en-IN');
}

function moneyLakh(value: number | null | undefined) {
  return value == null ? '—' : `₹ ${(value / 100_000).toLocaleString('en-IN', { maximumFractionDigits: 1 })} L`;
}

function percentageText(value: number | null | undefined) {
  return value == null ? '—' : `${value.toLocaleString('en-IN', { maximumFractionDigits: 1 })}%`;
}

function elapsedLabel(milliseconds: number) {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes ? `${minutes}m ${String(seconds).padStart(2, '0')}s` : `${seconds}s`;
}

function periodText(data: BusinessDashboardResponse | null) {
  return data?.selection?.from && data?.selection?.to
    ? `${data.selection.from} to ${data.selection.to}`
    : 'Selected period';
}

function dashboardKpis(data: BusinessDashboardResponse | null): { primary: BusinessKpi[]; secondary: BusinessKpi[] } {
  const kpis = data?.kpis;
  const targetGap = kpis?.revenueTarget != null && kpis.landing != null ? Math.max(0, kpis.revenueTarget - kpis.landing) : null;
  return {
    primary: [
      { label: 'Revenue', value: moneyLakh(kpis?.revenue), tone: 'purple', icon: <RupeeIcon />, ...(kpis?.revenueGrowthPercent != null ? { trend: percentageText(kpis.revenueGrowthPercent), trendDirection: kpis.revenueGrowthPercent < 0 ? 'down' as const : 'up' as const, comparison: 'MTD vs same period last month' } : {}), detail: periodText(data) },
      { label: 'Month Landing', value: moneyLakh(kpis?.landing), tone: 'cyan', icon: <LandingIcon />, detail: <>Target: <strong>{moneyLakh(kpis?.revenueTarget)}</strong></>, progress: kpis?.targetAchievementPercent ?? undefined, progressLabel: percentageText(kpis?.targetAchievementPercent) },
      { label: 'Target Achievement', value: percentageText(kpis?.targetAchievementPercent), tone: 'pink', icon: <TargetIcon />, note: targetGap == null ? 'Available for current-month target scopes' : <>Gap: <strong>{moneyLakh(targetGap)}</strong></> },
      { label: 'New Registrations', value: numberText(kpis?.registrations), tone: 'blue', icon: <PatientIcon />, detail: periodText(data) },
      { label: 'NPS Score', value: numberText(kpis?.nps?.score), tone: 'amber', icon: <StarIcon />, detail: kpis?.nps ? <>{numberText(kpis.nps.responses)} governed responses</> : 'No responses in the selected period' },
    ],
    secondary: [
      { label: 'IVF Advice', value: numberText(kpis?.ivfAdvice), tone: 'violet', icon: <AdviceIcon />, detail: periodText(data) },
      { label: 'OPU Billed', value: numberText(kpis?.opuBilled), tone: 'green', icon: <PeopleIcon />, detail: periodText(data) },
      {
        label: 'IVF Conversion',
        value: percentageText(kpis?.ivfConversionPercent),
        tone: 'orange',
        icon: <PercentIcon />,
        detail: <>Target: <strong>{percentageText(kpis?.ivfConversionTargetPercent)}</strong></>,
        note: 'Fresh OPU ÷ registrations',
        progress: kpis?.ivfConversionAchievementPercent ?? undefined,
        progressLabel: percentageText(kpis?.ivfConversionAchievementPercent),
      },
    ],
  };
}

function acquisitionSeries(data: BusinessDashboardResponse | null, selectedChannel: string) {
  const rows = data?.acquisition?.daily ?? [];
  const candidates = [
    { key: 'B2B', label: 'B2B', color: '#4f7ee8', value: (row: (typeof rows)[number]) => row.b2b },
    { key: 'B2C', label: 'B2C', color: '#e91e63', value: (row: (typeof rows)[number]) => row.b2c },
    { key: 'BTL', label: 'BTL', color: '#f59e0b', value: (row: (typeof rows)[number]) => row.btl },
  ];
  return candidates.filter((series) => selectedChannel === 'ALL' || series.key === selectedChannel).map((series) => ({ label: series.label, color: series.color, values: rows.map((row) => ({ date: row.date, value: series.value(row) })) }));
}

function funnelStages(data: BusinessDashboardResponse | null) {
  const registrations = data?.funnel?.registrations ?? null;
  const consultations = data?.funnel?.consultations ?? null;
  const advice = data?.funnel?.ivfAdvice ?? null;
  const stimulation = data?.funnel?.stimulationStarted ?? null;
  const opuBilled = data?.funnel?.opuBilled ?? null;
  const freezingRevenue = data?.funnel?.freezingChargeRevenue ?? null;
  const freezingPatients = data?.funnel?.freezingPatients ?? null;
  const ratio = (value: number | null, base: number | null) => value != null && base && base > 0 ? Math.min(100, Math.round(value / base * 1000) / 10) : null;
  return [
    { label: 'Registrations', value: numberText(registrations), percent: registrations == null ? '—' : '100%', width: registrations == null ? 0 : 100, tone: 'blue', icon: <PatientIcon /> },
    { label: 'Consultations', value: numberText(consultations), percent: percentageText(ratio(consultations, registrations)), width: ratio(consultations, registrations) ?? 0, tone: 'violet', icon: <PeopleIcon /> },
    { label: 'IVF Advice', value: numberText(advice), percent: percentageText(ratio(advice, registrations)), width: ratio(advice, registrations) ?? 0, tone: 'amber', icon: <FunnelIcon /> },
    { label: 'Stimulation Started', value: numberText(stimulation), percent: percentageText(ratio(stimulation, registrations)), width: ratio(stimulation, registrations) ?? 0, tone: 'pink', icon: <PatientIcon /> },
    { label: 'OPU Billed', value: numberText(opuBilled), percent: percentageText(ratio(opuBilled, registrations)), width: ratio(opuBilled, registrations) ?? 0, tone: 'green', icon: <HourglassIcon /> },
    { label: 'Freezing Charges', value: moneyLakh(freezingRevenue), percent: percentageText(ratio(freezingPatients, registrations)), width: ratio(freezingPatients, registrations) ?? 0, tone: 'cyan', icon: <RupeeIcon /> },
  ];
}

const DEFAULT_DASHBOARD_QUERY: BusinessDashboardQuery = { period: 'CURRENT_MONTH' };
const BUSINESS_DASHBOARD_VIEW_CACHE_TTL_MS = 60 * 60 * 1_000;
const BUSINESS_DASHBOARD_VIEW_CACHE_MAX_ENTRIES = 8;

type BusinessDashboardViewCacheEntry = {
  data: BusinessDashboardResponse;
  elapsedMs: number;
  loadedAt: number;
};

// Keep recently rendered views across normal client-side report navigation.
// Authentication changes clear the entire map before any entry can be read.
const businessDashboardViews = new Map<string, BusinessDashboardViewCacheEntry>();
let businessDashboardViewOwnerToken: string | undefined;

function businessDashboardQueryKey(query: BusinessDashboardQuery) {
  return JSON.stringify({
    period: query.period,
    from: query.from || '',
    to: query.to || '',
    center: query.center || 'ALL',
    channel: query.channel || 'ALL',
  });
}

function retainDashboardView(
  cache: Map<string, BusinessDashboardViewCacheEntry>,
  key: string,
  entry: BusinessDashboardViewCacheEntry,
) {
  cache.delete(key);
  cache.set(key, entry);
  while (cache.size > BUSINESS_DASHBOARD_VIEW_CACHE_MAX_ENTRIES) {
    const oldest = cache.keys().next().value as string | undefined;
    if (!oldest) break;
    cache.delete(oldest);
  }
}

const BUSINESS_DATE_PRESETS: Array<{ value: BusinessDashboardPeriod; label: string }> = [
  { value: 'CURRENT_MONTH', label: 'Current Month' },
  { value: 'PREVIOUS_MONTH', label: 'Previous Month' },
  { value: 'LAST_3_MONTHS', label: 'Last 3 Months' },
  { value: 'PREVIOUS_3_MONTHS', label: 'Previous 3 Months' },
  { value: 'CURRENT_WEEK', label: 'Current Week' },
  { value: 'LAST_WEEK', label: 'Last Week' },
  { value: 'CURRENT_QUARTER', label: 'Current Quarter' },
  { value: 'LAST_QUARTER', label: 'Last Quarter' },
  { value: 'LAST_7_DAYS', label: 'Last 7 Days' },
  { value: 'LAST_30_DAYS', label: 'Last 30 Days' },
  { value: 'LAST_90_DAYS', label: 'Last 90 Days' },
  { value: 'YTD', label: 'YTD (Year to Date)' },
  { value: 'FY_YTD', label: 'FY-YTD (Financial Year to Date)' },
  { value: 'CUSTOM', label: 'Custom Range' },
  { value: 'LAST_YEAR', label: 'Last Year' },
  { value: 'ALL_TIME', label: 'All Time' },
];

export function BusinessDashboardTab({
  active,
  token,
  canViewAncPatients,
  canExportAncPatients,
  notify,
}: {
  active: boolean;
  token?: string;
  canViewAncPatients: boolean;
  canExportAncPatients: boolean;
  notify: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void;
}) {
  const [outlookOpen, setOutlookOpen] = useState(false);
  const [funnelOpen, setFunnelOpen] = useState(false);
  const [ancOpen, setAncOpen] = useState(false);
  const [ancData, setAncData] = useState<BusinessDashboardAncPatientsResponse | null>(null);
  const [ancLoading, setAncLoading] = useState(false);
  const [ancError, setAncError] = useState<string | null>(null);
  const [ancSearch, setAncSearch] = useState('');
  const [appliedAncSearch, setAppliedAncSearch] = useState('');
  const [ancPage, setAncPage] = useState(1);
  const [ancExporting, setAncExporting] = useState(false);
  const [dashboardPeriod, setDashboardPeriod] = useState<BusinessDashboardPeriod>('CURRENT_MONTH');
  const [dashboardCenter, setDashboardCenter] = useState('ALL');
  const [dashboardChannel, setDashboardChannel] = useState('ALL');
  const [dashboardFrom, setDashboardFrom] = useState('');
  const [dashboardTo, setDashboardTo] = useState('');
  const [appliedQuery, setAppliedQuery] = useState<BusinessDashboardQuery>(DEFAULT_DASHBOARD_QUERY);
  const [data, setData] = useState<BusinessDashboardResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState<number | null>(null);
  const loadStartedAt = useRef<number | null>(null);
  const forceRefreshNextApply = useRef(false);
  const pendingForceRefresh = useRef(false);
  const [requestVersion, setRequestVersion] = useState(0);
  const appliedQueryKey = businessDashboardQueryKey(appliedQuery);

  useEffect(() => {
    if (!loading || loadStartedAt.current === null) return undefined;
    const updateElapsed = () => setElapsedMs(Date.now() - (loadStartedAt.current || Date.now()));
    updateElapsed();
    const timer = window.setInterval(updateElapsed, 1_000);
    return () => window.clearInterval(timer);
  }, [loading]);

  useEffect(() => {
    if (!active) return undefined;
    if (businessDashboardViewOwnerToken !== token) {
      businessDashboardViews.clear();
      businessDashboardViewOwnerToken = token;
      setData(null);
    }
    const forceRefresh = pendingForceRefresh.current;
    pendingForceRefresh.current = false;
    const cached = forceRefresh ? undefined : businessDashboardViews.get(appliedQueryKey);
    if (cached && Date.now() - cached.loadedAt < BUSINESS_DASHBOARD_VIEW_CACHE_TTL_MS) {
      setData(cached.data);
      setElapsedMs(cached.elapsedMs);
      setLoading(false);
      setLoadError(null);
      return undefined;
    }
    if (cached) businessDashboardViews.delete(appliedQueryKey);
    let cancelled = false;
    const controller = new AbortController();
    loadStartedAt.current = Date.now();
    setElapsedMs(0);
    setLoading(true);
    setLoadError(null);
    getBusinessDashboard(token, forceRefresh ? { ...appliedQuery, refresh: true } : appliedQuery, controller.signal)
      .then((response) => {
        if (!cancelled) {
          const completedInMs = Date.now() - (loadStartedAt.current || Date.now());
          if (response.status === 'READY') {
            retainDashboardView(businessDashboardViews, appliedQueryKey, {
              data: response,
              elapsedMs: completedInMs,
              loadedAt: Date.now(),
            });
          }
          setData(response);
          setElapsedMs(completedInMs);
          loadStartedAt.current = null;
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setData(null);
          setElapsedMs(null);
          loadStartedAt.current = null;
          setLoadError(error instanceof Error ? error.message : 'Business Dashboard could not be loaded.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
      loadStartedAt.current = null;
    };
  }, [active, appliedQueryKey, requestVersion, token]);

  useEffect(() => {
    if (!active || !ancOpen || !canViewAncPatients) return undefined;
    let cancelled = false;
    setAncLoading(true);
    setAncError(null);
    getBusinessDashboardAncPatients(token, {
      ...appliedQuery,
      page: ancPage,
      pageSize: 25,
      ...(appliedAncSearch ? { search: appliedAncSearch } : {}),
    })
      .then((response) => {
        if (!cancelled) setAncData(response);
      })
      .catch((error: unknown) => {
        if (!cancelled) setAncError(error instanceof Error ? error.message : 'ANC patient evidence could not be loaded.');
      })
      .finally(() => {
        if (!cancelled) setAncLoading(false);
      });
    return () => { cancelled = true; };
  }, [active, ancOpen, canViewAncPatients, token, appliedQuery, ancPage, appliedAncSearch]);

  useEffect(() => {
    if (!outlookOpen && !funnelOpen && !ancOpen) return undefined;
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOutlookOpen(false);
        setFunnelOpen(false);
        setAncOpen(false);
      }
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [outlookOpen, funnelOpen, ancOpen]);

  const exportAncEvidence = async () => {
    if (!canExportAncPatients || ancExporting) return;
    setAncExporting(true);
    try {
      const result = await downloadBusinessDashboardAncPatients(token, appliedQuery);
      const url = URL.createObjectURL(result.blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = result.fileName || 'Ferty9_ANC_Patient_Revenue.xlsx';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      notify('Detailed ANC patient revenue workbook downloaded.', 'success');
    } catch (error) {
      notify(error instanceof Error ? error.message : 'ANC workbook could not be downloaded.', 'error');
    } finally {
      setAncExporting(false);
    }
  };

  const kpiRows = dashboardKpis(data);

  const resetDashboardFilters = () => {
    setDashboardPeriod('CURRENT_MONTH');
    setDashboardCenter('ALL');
    setDashboardChannel('ALL');
    setDashboardFrom('');
    setDashboardTo('');
    forceRefreshNextApply.current = true;
  };

  const applyDashboardFilters = () => {
    pendingForceRefresh.current = forceRefreshNextApply.current;
    forceRefreshNextApply.current = false;
    setAppliedQuery({
      period: dashboardPeriod,
      ...(dashboardPeriod === 'CUSTOM' ? { from: dashboardFrom, to: dashboardTo } : {}),
      ...(dashboardCenter !== 'ALL' ? { center: dashboardCenter } : {}),
      ...(dashboardChannel !== 'ALL' ? { channel: dashboardChannel } : {}),
    });
    setRequestVersion((version) => version + 1);
  };

  return (
    <section
      className="tab-content enterprise-page business-dashboard-page"
      style={{ display: active ? 'grid' : 'none' }}
      aria-label="Business Dashboard"
    >
      <section className="card business-dashboard-filter-card" aria-label="Business Dashboard filters">
        <div className="business-dashboard-filter-heading">
          <div><strong>Business dashboard filters</strong><span>One shared scope will control revenue, acquisition, stimulation and FET insights.</span></div>
          <div className="business-dashboard-filter-state">
            <span className={`dashboard-load-timer${loading ? ' running' : ''}`} role="status" aria-live="polite">{loading ? 'Loading' : 'Loaded in'} <strong>{elapsedMs === null ? 'Not loaded' : elapsedLabel(elapsedMs)}</strong></span>
            <span className={`business-dashboard-filter-status ${loading || !data ? 'pending' : data.status === 'READY' ? 'ready' : data.status === 'NO_DATA' ? 'no-data' : 'partial'}`}>{loading ? 'Data Loading' : !data ? 'Data Not Loaded' : data.status === 'READY' ? 'Data Ready' : data.status === 'NO_DATA' ? 'No Data' : 'Data Partial'}</span>
          </div>
        </div>
        <div className={`business-dashboard-filter-grid${dashboardPeriod === 'CUSTOM' ? ' has-custom-range' : ''}`}>
          <label><span>Date period</span><select value={dashboardPeriod} onChange={(event) => setDashboardPeriod(event.target.value as BusinessDashboardPeriod)}>{BUSINESS_DATE_PRESETS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
          {dashboardPeriod === 'CUSTOM' ? <><label><span>From</span><input type="date" value={dashboardFrom} onChange={(event) => setDashboardFrom(event.target.value)} /></label><label><span>To</span><input type="date" value={dashboardTo} onChange={(event) => setDashboardTo(event.target.value)} /></label></> : null}
          <label><span>Centre</span><select value={dashboardCenter} onChange={(event) => setDashboardCenter(event.target.value)}><option value="ALL">All permitted centres</option>{data?.filterOptions?.centers?.map((center) => <option key={center.code} value={center.code}>{center.label}</option>)}</select></label>
          <label><span>Acquisition channel</span><select value={dashboardChannel} onChange={(event) => setDashboardChannel(event.target.value)}><option value="ALL">All channels</option><option value="B2B">B2B</option><option value="B2C">B2C</option><option value="BTL">BTL</option></select></label>
          <div className="business-dashboard-filter-actions">
            <button className="btn btn-outline" type="button" disabled={loading} onClick={resetDashboardFilters}>Reset filters</button>
            <button className="btn btn-primary" type="button" disabled={loading || (dashboardPeriod === 'CUSTOM' && (!dashboardFrom || !dashboardTo || dashboardFrom > dashboardTo))} onClick={applyDashboardFilters}>{loading ? 'Applying…' : 'Apply filters'}</button>
          </div>
        </div>
      </section>

      {loadError ? <div className="alert alert-error" role="alert">{loadError}</div> : null}
      {data?.coverage?.failures?.length ? <div className="alert alert-warning" role="status">{data.coverage.message}</div> : null}

      <div className="business-outlook-strip" role="status" aria-label="Business outlook policy pending">
        <span className="business-outlook-check"><CheckIcon /></span>
        <div className="business-outlook-copy">
          <strong>Business Outlook: <span>Awaiting policy</span></strong>
          <p>Governed business metrics are connected. Automated outlook commentary will activate after management thresholds are approved.</p>
        </div>
        <button className="btn btn-outline business-outlook-action" type="button" onClick={() => setOutlookOpen(true)}>View Details <ArrowRightIcon /></button>
      </div>

      <div className="business-dashboard-kpi-grid" aria-label="Business Dashboard key performance indicators">
        {kpiRows.primary.map((item) => <BusinessKpiCard key={item.label} item={item} />)}
      </div>

      <div className="business-dashboard-operational-kpi-grid" aria-label="Business Dashboard operational key performance indicators">
        <BusinessKpiCard item={kpiRows.secondary[0]!} />
        <BusinessPendingKpi connected label="Stimulation Started" value={numberText(data?.kpis?.stimulationStarted)} tone="green" icon={<LandingIcon />} detail={periodText(data)} note="Distinct governed stimulation cycles" />
        <BusinessKpiCard item={kpiRows.secondary[1]!} />
        <BusinessKpiCard item={kpiRows.secondary[2]!} />
        <BusinessPendingKpi connected label="FET Completed" value={numberText(data?.kpis?.fetCompleted)} tone="pink" icon={<HourglassIcon />} detail={<><strong>1st FET</strong> {numberText(data?.kpis?.firstFet)} <i /> <strong>FET</strong> {numberText(data?.kpis?.repeatFet)} <i /> <strong>Pending</strong> {numberText(data?.kpis?.fetMappingPending)}</>} note="Valid cycles by transfer date" />
      </div>

      <div className="business-dashboard-strategic-kpi-grid" aria-label="Business Dashboard strategic clinical key performance indicators">
        <BusinessPendingKpi connected label="IVF Failed" value={numberText(data?.kpis?.ivfFailed)} tone="pink" icon={<HourglassIcon />} detail={periodText(data)} note="Completed IVF cycles with no eggs retrieved" />
        <BusinessPendingKpi connected label="ANC Patients" value={numberText(data?.kpis?.ancPatients)} tone="cyan" icon={<PatientIcon />} detail={periodText(data)} note={`ANC business value ${moneyLakh(data?.kpis?.ancBusinessValue)}`} action={canViewAncPatients ? { label: 'View Patients', onClick: () => { setAncPage(1); setAncOpen(true); } } : undefined} metrics={[
          { label: 'ANC <20', value: numberText(data?.kpis?.ancWithin20Weeks) },
          { label: 'ANC 20–42', value: numberText(data?.kpis?.ancBeyond20Weeks) },
          { label: 'Week pending', value: numberText(data?.kpis?.ancWeekPending) },
        ]} />
        <BusinessPendingKpi connected label="PGT Coverage" value={percentageText(data?.kpis?.pgtCoveragePercent)} tone="violet" icon={<TargetIcon />} detail={<><strong>PGT cycles</strong> {numberText(data?.kpis?.pgtCycles)} <i /> <strong>Eligible</strong> {numberText(data?.kpis?.pgtEligibleCycles)}</>} note="PGT-selected ÷ eligible IVF starts" />
      </div>

      <div className="business-dashboard-insight-grid" aria-label="Business Dashboard primary insights">
        <div className="card clinical-chart-card business-revenue-card">
          <div className="business-dashboard-card-heading business-revenue-heading">
            <div><strong>Revenue Performance &amp; Outlook</strong><span>Historical Trend {'\u2022'} MTD {'\u2022'} Forecast {'\u2022'} Target {'\u2022'} Revenue ({'\u20B9'} Lakh)</span></div>
            <div className="business-chart-legend" aria-label="Revenue chart legend">
              <span className="actual"><i />Actual</span><span className="landing"><i />Landing</span><span className="target"><i />Target</span>
            </div>
          </div>
          <div className="clinical-chart-body"><BusinessRevenueTrend data={data} /></div>
        </div>
        <div className="card clinical-chart-card business-funnel-card">
          <div className="business-dashboard-card-heading">
            <div><strong>Patient to IVF Funnel (MTD)</strong><span>Prospective MTD revenue pipelines</span></div>
            <button className="business-card-action" type="button" onClick={() => setFunnelOpen(true)} disabled={!data}>View Details <ArrowRightIcon /></button>
          </div>
          <div className="clinical-chart-body"><BusinessPatientFunnel data={data} /></div>
        </div>
        <div className="card clinical-chart-card business-health-card">
          <div className="business-dashboard-card-heading">
            <div><strong>Business Health</strong><span>Status of key business indicators.</span></div>
          </div>
          <div className="clinical-chart-body"><BusinessPolicyPending message="Management thresholds are awaiting approval." /></div>
        </div>
      </div>

      <div className="business-centre-row" aria-label="Business Dashboard centre performance">
        <BusinessCentrePerformance rows={data?.centres ?? []} overallConversionPercent={data?.kpis?.ivfConversionPercent ?? null} />
      </div>

      <BusinessRevenueByService revenue={data?.revenueByService ?? null} />

      <div className="business-dashboard-new-insight-grid" aria-label="Business Dashboard daily and revenue-mix insights">
        <BusinessConnectedTrendCard
          title="Daily Revenue Trend (MTD)"
          description="Daily governed revenue across the selected MTD period."
          series={[{ label: 'Revenue', color: '#e91e63', values: (data?.dailyRevenue ?? []).map((row) => ({ date: row.date, value: row.revenueRupees / 100_000 })) }]}
          valueSuffix=" L"
        />
        <BusinessConnectedTrendCard
          title="B2B, B2C & BTL Patient Acquisition Trend (MTD)"
          description="Daily governed registrations by acquisition channel."
          series={acquisitionSeries(data, dashboardChannel)}
        />
      </div>

      <div className="business-secondary-grid" aria-label="Business Dashboard pipeline, channel and patient experience insights">
        <BusinessOpportunityPipeline />
        <BusinessChannelPerformance revenue={data?.revenueByChannel ?? null} />
        <BusinessPatientExperience nps={data?.nps ?? null} />
      </div>

      <div className="business-dashboard-strategic-grid" aria-label="Business Dashboard strategic clinical insights by centre">
        <BusinessStrategicBarCard
          id="business-ivf-failures-by-centre-treatment"
          title="IVF Failures by Centre & Treatment Type"
          description="Completed IVF cycles with no eggs retrieved."
          labels={(data?.clinicalInsights?.ivfFailuresByCenterAndTreatment ?? []).map((row) => `${shortBusinessCenter(row.center)} · ${row.treatment}`)}
          values={(data?.clinicalInsights?.ivfFailuresByCenterAndTreatment ?? []).map((row) => row.failures)}
          color="#e91e63"
          empty={data?.kpis?.ivfFailed === null
            ? 'IVF failure evidence is unavailable for the selected scope.'
            : 'No completed IVF cycles with zero eggs match the selected scope.'}
        />
        <BusinessStrategicBarCard
          id="business-anc-beyond-20-weeks-by-centre"
          title="ANC >20 Weeks by Centre"
          description="Selected-period ANC service patients at 20–42 completed weeks."
          labels={(data?.clinicalInsights?.ancBeyond20WeeksByCenter ?? []).map((row) => shortBusinessCenter(row.center))}
          values={(data?.clinicalInsights?.ancBeyond20WeeksByCenter ?? []).map((row) => row.cases)}
          color="#14b8a6"
          empty="No qualifying ANC cases match the selected scope."
        />
        <BusinessStrategicBarCard
          id="business-pgt-coverage-by-centre"
          title="PGT Coverage by Centre"
          description="PGT-selected cycles ÷ eligible IVF starts."
          labels={(data?.clinicalInsights?.pgtCoverageByCenter ?? []).map((row) => shortBusinessCenter(row.center))}
          values={(data?.clinicalInsights?.pgtCoverageByCenter ?? []).map((row) => row.coveragePercent)}
          color="#8b5cf6"
          valueSuffix="%"
          percentageScale
          empty="No eligible IVF starts match the selected scope."
        />
      </div>

      <div className="business-bottom-insight-grid" aria-label="Business Dashboard doctor, growth and management insights">
        <BusinessDoctorPerformance rows={data?.doctors ?? []} />
        <BusinessGrowthLeaders rows={data?.growthLeaders ?? []} />
        <BusinessTargetGapContributors rows={data?.gapDrivers ?? []} />
        <BusinessManagementAttention />
      </div>

      {outlookOpen && (
        <div className="enterprise-drawer-backdrop" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) setOutlookOpen(false); }}>
          <aside className="enterprise-drawer business-outlook-drawer" role="dialog" aria-modal="true" aria-label="Business outlook details">
            <button className="drawer-close" type="button" aria-label="Close business outlook details" onClick={() => setOutlookOpen(false)}>{'\u00D7'}</button>
            <p className="enterprise-eyebrow">Business Dashboard</p>
            <h3>Business Outlook Details</h3>
            <p>Governed dashboard data is ready. Automated interpretation remains paused until management approves the business thresholds.</p>
            <div className="business-outlook-detail-list">
              <div><span>Data readiness</span><strong>{data?.status === 'READY' ? 'Data Ready' : data?.status === 'PARTIAL_DATA' ? 'Data Partial' : 'No Data'}</strong></div>
              <div><span>Revenue and centre performance</span><strong>Connected</strong></div>
              <div><span>Patient acquisition and clinical funnel</span><strong>Connected</strong></div>
              <div><span>NPS response metrics</span><strong>Connected</strong></div>
              <div><span>Automated outlook policy</span><strong>Awaiting approval</strong></div>
            </div>
            {data?.pendingCapabilities?.length ? <div className="business-outlook-pending"><strong>Pending definitions</strong><ul>{data.pendingCapabilities.map((item) => <li key={item}>{item}</li>)}</ul></div> : null}
          </aside>
        </div>
      )}

      {funnelOpen && (
        <div className="enterprise-drawer-backdrop" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) setFunnelOpen(false); }}>
          <aside className="enterprise-drawer business-outlook-drawer" role="dialog" aria-modal="true" aria-label="Patient to IVF funnel details">
            <button className="drawer-close" type="button" aria-label="Close funnel details" onClick={() => setFunnelOpen(false)}>{'\u00D7'}</button>
            <p className="enterprise-eyebrow">Business Dashboard</p>
            <h3>Patient to IVF Funnel</h3>
            <p>Prospective revenue opportunities across the current-month patient pipeline and freezing charges.</p>
            <div className="business-funnel-drawer-list">
              {funnelStages(data).map((stage) => <div key={stage.label}><span>{stage.label}</span><strong>{stage.value}</strong><em>{stage.percent}</em></div>)}
            </div>
          </aside>
        </div>
      )}

      {ancOpen && canViewAncPatients ? (
        <AncPatientsDrawer
          data={ancData}
          loading={ancLoading}
          error={ancError}
          search={ancSearch}
          exporting={ancExporting}
          canExport={canExportAncPatients}
          onSearchChange={setAncSearch}
          onSearch={() => { setAncPage(1); setAppliedAncSearch(ancSearch.trim()); }}
          onClearSearch={() => { setAncSearch(''); setAppliedAncSearch(''); setAncPage(1); }}
          onPage={setAncPage}
          onExport={() => { void exportAncEvidence(); }}
          onClose={() => setAncOpen(false)}
        />
      ) : null}

    </section>
  );
}

function BusinessPendingKpi({ label, value, tone, icon, detail, note, connected = false, metrics, action }: { label: string; value: string; tone: BusinessKpiTone; icon: ReactNode; detail: ReactNode; note: string; connected?: boolean; metrics?: Array<{ label: string; value: string }>; action?: { label: string; onClick: () => void } }) {
  const accentStyle = { '--business-kpi-accent': KPI_TONES[tone] } as CSSProperties;
  return <article className={`business-dashboard-kpi-card business-dashboard-pending-kpi tone-${tone}`} style={accentStyle}>
    <div className="business-dashboard-kpi-head"><span className="business-dashboard-kpi-icon" aria-hidden="true">{icon}</span><span>{label}</span></div>
    <div className="business-dashboard-kpi-primary"><strong className="business-dashboard-kpi-value">{value}</strong>{connected ? null : <span className="business-pending-badge">Awaiting source</span>}</div>
    {metrics?.length ? <dl className="clinical-kpi-inline-metrics business-anc-kpi-metrics">{metrics.map((metric) => <div key={metric.label}><dt>{metric.label}</dt><dd>{metric.value}</dd></div>)}</dl> : null}
    <div className="business-pending-kpi-footer"><span>{detail}</span><small>{note}</small>{action ? <button className="business-kpi-link" type="button" onClick={action.onClick}>{action.label} <ArrowRightIcon /></button> : null}</div>
  </article>;
}

function AncPatientsDrawer({
  data,
  loading,
  error,
  search,
  exporting,
  canExport,
  onSearchChange,
  onSearch,
  onClearSearch,
  onPage,
  onExport,
  onClose,
}: {
  data: BusinessDashboardAncPatientsResponse | null;
  loading: boolean;
  error: string | null;
  search: string;
  exporting: boolean;
  canExport: boolean;
  onSearchChange: (value: string) => void;
  onSearch: () => void;
  onClearSearch: () => void;
  onPage: (page: number) => void;
  onExport: () => void;
  onClose: () => void;
}) {
  const summary = data?.summary;
  return (
    <div className="enterprise-drawer-backdrop" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) onClose(); }}>
      <aside className="enterprise-drawer business-anc-drawer" role="dialog" aria-modal="true" aria-label="ANC patient revenue evidence">
        <button className="drawer-close" type="button" aria-label="Close ANC patient records" onClick={onClose}>{'\u00D7'}</button>
        <div className="business-anc-drawer-heading">
          <div>
            <p className="enterprise-eyebrow">Business Dashboard</p>
            <h3>ANC Patients</h3>
            <p>Patient-level preview of governed Hospital and Medics revenue for the selected dashboard scope.</p>
          </div>
          {canExport ? <button className="btn btn-primary" type="button" disabled={loading || exporting} onClick={onExport}>{exporting ? 'Preparing Excel…' : 'Download Detailed Excel'}</button> : null}
        </div>

        <div className="business-anc-summary-grid" aria-label="ANC patient and revenue totals">
          <AncSummaryTile label="Total patients" value={numberText(summary?.ancPatients)} />
          <AncSummaryTile label="ANC <20 weeks" value={numberText(summary?.ancWithin20Weeks)} />
          <AncSummaryTile label="ANC 20–42 weeks" value={numberText(summary?.ancBeyond20Weeks)} />
          <AncSummaryTile label="Week pending" value={numberText(summary?.ancWeekPending)} />
          <AncSummaryTile label="Hospital revenue" value={moneyExact(summary?.ancHospitalRevenue)} />
          <AncSummaryTile label="Medics revenue" value={moneyExact(summary?.ancMedicsRevenue)} />
          <AncSummaryTile label="Combined revenue" value={moneyExact(summary?.ancBusinessValue)} emphasis />
        </div>

        {data?.coverage.failures.length ? <div className="alert alert-warning" role="status">{data.coverage.message}</div> : null}
        {error ? <div className="alert alert-error" role="alert">{error}</div> : null}

        <form className="business-anc-search" onSubmit={(event) => { event.preventDefault(); onSearch(); }}>
          <label htmlFor="business-anc-patient-search">Search patient records</label>
          <div>
            <input id="business-anc-patient-search" type="search" value={search} onChange={(event) => onSearchChange(event.target.value)} placeholder="Patient name, patient ID, MRN or centre" />
            {search ? <button className="btn btn-outline" type="button" onClick={onClearSearch}>Clear</button> : null}
            <button className="btn btn-outline" type="submit" disabled={loading}>Search</button>
          </div>
        </form>

        <div className="business-anc-table-wrap">
          <table className="business-anc-table">
            <thead><tr><th>Patient / MRN</th><th>Centre</th><th>ANC stage</th><th>Hospital</th><th>Medics</th><th>Combined</th><th>Invoices</th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={7} className="business-anc-table-state">Loading governed ANC evidence…</td></tr> : null}
              {!loading && !data?.patients.length ? <tr><td colSpan={7} className="business-anc-table-state">No matching ANC patients were found.</td></tr> : null}
              {!loading ? data?.patients.map((patient) => (
                <tr key={patient.patientKey}>
                  <td><strong>{patient.patientName || patient.patientId || patient.clinicMrn || 'Patient'}</strong><span>{[patient.patientId, patient.clinicMrn].filter(Boolean).join(' · ') || 'Identifier unavailable'}</span></td>
                  <td>{patient.centers.map(shortBusinessCenter).join(', ')}</td>
                  <td><span className={`business-anc-stage ${patient.stage.toLowerCase()}`}>{ancStageText(patient.stage)}</span></td>
                  <td>{moneyExact(patient.hospitalRevenue)}</td>
                  <td>{moneyExact(patient.medicsRevenue)}</td>
                  <td><strong>{moneyExact(patient.combinedRevenue)}</strong></td>
                  <td>{numberText(patient.invoiceCount)}<span>{patient.hospitalInvoiceCount} hospital · {patient.medicsInvoiceCount} Medics</span></td>
                </tr>
              )) : null}
            </tbody>
          </table>
        </div>

        <div className="business-anc-pagination">
          <span>{data ? `${data.pagination.total.toLocaleString('en-IN')} patients · Page ${data.pagination.page} of ${data.pagination.pages}` : 'Patient evidence'}</span>
          <div>
            <button className="btn btn-outline" type="button" disabled={loading || !data || data.pagination.page <= 1} onClick={() => onPage((data?.pagination.page || 1) - 1)}>Previous</button>
            <button className="btn btn-outline" type="button" disabled={loading || !data || data.pagination.page >= data.pagination.pages} onClick={() => onPage((data?.pagination.page || 1) + 1)}>Next</button>
          </div>
        </div>
      </aside>
    </div>
  );
}

function AncSummaryTile({ label, value, emphasis = false }: { label: string; value: string; emphasis?: boolean }) {
  return <div className={emphasis ? 'emphasis' : ''}><span>{label}</span><strong>{value}</strong></div>;
}

function moneyExact(value: number | null | undefined) {
  return value == null ? '—' : `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function ancStageText(stage: 'WITHIN_20' | 'BEYOND_20' | 'PENDING') {
  if (stage === 'WITHIN_20') return '<20 weeks';
  if (stage === 'BEYOND_20') return '20–42 weeks';
  return 'Week pending';
}

function BusinessPolicyPending({ message }: { message: string }) {
  return <div className="business-pending-chart-empty"><TargetIcon /><strong>Policy definition pending</strong><span>{message}</span></div>;
}

type ConnectedSeries = { label: string; color: string; values: Array<{ date: string; value: number }> };

function BusinessChartCanvas({ id, config, empty }: { id: string; config: Record<string, unknown>; empty?: string }) {
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const chart = useRef<BusinessChartInstance | null>(null);
  useEffect(() => {
    if (!canvas.current || empty) return undefined;
    let disposed = false;
    void loadChartConstructor().then((Chart) => {
      if (disposed || !canvas.current) return;
      chart.current?.destroy();
      chart.current = new (Chart as BusinessChartConstructor)(canvas.current, config);
    });
    return () => {
      disposed = true;
      chart.current?.destroy();
      chart.current = null;
    };
  }, [config, empty]);
  if (empty) return <div className="clinical-chart-empty"><strong>No matching governed data</strong><span>{empty}</span></div>;
  return <canvas id={id} ref={canvas} />;
}

function BusinessConnectedTrendCard({ title, description, series, valueSuffix = '' }: { title: string; description: string; series: ConnectedSeries[]; valueSuffix?: string }) {
  const dates = [...new Set(series.flatMap((item) => item.values.map((point) => point.date)))].sort();
  const config = useMemo(() => {
    const rows = series.map((item) => ({ ...item, valuesByDate: new Map(item.values.map((point) => [point.date, point.value])) }));
    return {
      type: 'line',
      data: {
        labels: dates.map(formatBusinessTrendDate),
        datasets: rows.map((item, index) => ({
          label: item.label,
          data: dates.map((date) => item.valuesByDate.get(date) ?? 0),
          borderColor: item.color,
          backgroundColor: `${item.color}${index === 0 ? '1f' : '14'}`,
          pointBackgroundColor: item.color,
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 7,
          borderWidth: 3,
          tension: 0.32,
          fill: true,
        })),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { intersect: false, mode: 'index' },
        plugins: {
          legend: { position: 'bottom' },
          tooltip: valueSuffix ? { callbacks: { label: (context: { dataset: { label: string }; parsed: { y: number } }) => `${context.dataset.label}: ${context.parsed.y.toLocaleString('en-IN', { maximumFractionDigits: 1 })}${valueSuffix}` } } : undefined,
        },
        scales: { y: { beginAtZero: true, ticks: { precision: valueSuffix ? 1 : 0 } }, x: { grid: { display: false } } },
      },
    };
  }, [dates.join('|'), series, valueSuffix]);
  return <article className="card clinical-chart-card business-connected-trend-card">
    <div><strong>{title}</strong><span>{description}</span></div>
    <div className="clinical-chart-body"><BusinessChartCanvas id={`business-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} config={config} empty={!dates.length ? 'Adjust the reporting period or centre selection.' : undefined} /></div>
  </article>;
}

function shortBusinessCenter(value: string) {
  return value.replace(/^Ferty9 Fertility Center\s*-\s*/i, '');
}

function BusinessStrategicBarCard({
  id,
  title,
  description,
  labels,
  values,
  color,
  valueSuffix = '',
  percentageScale = false,
  empty,
}: {
  id: string;
  title: string;
  description: string;
  labels: string[];
  values: number[];
  color: string;
  valueSuffix?: string;
  percentageScale?: boolean;
  empty: string;
}) {
  const config = useMemo(() => {
    const valueLabels = {
      id: `${id}-value-labels`,
      afterDatasetsDraw: (chart: any) => {
        const { ctx, chartArea } = chart;
        ctx.save();
        ctx.font = '800 11px Inter, system-ui, sans-serif';
        ctx.textBaseline = 'middle';
        chart.getDatasetMeta(0).data.forEach((bar: { x: number; y: number; base: number }, index: number) => {
          const value = values[index];
          if (value == null) return;
          const label = `${value.toLocaleString('en-IN', { maximumFractionDigits: 1 })}${valueSuffix}`;
          const inside = bar.x - bar.base > Math.max(34, label.length * 7);
          ctx.fillStyle = inside ? '#ffffff' : '#334155';
          ctx.textAlign = inside ? 'right' : 'left';
          ctx.fillText(label, inside ? bar.x - 7 : Math.min(chartArea.right - 2, bar.x + 7), bar.y);
        });
        ctx.restore();
      },
    };
    return {
      type: 'bar',
      plugins: [valueLabels],
      data: {
        labels,
        datasets: [{ label: title, data: values, backgroundColor: color, borderColor: color, borderWidth: 1, borderRadius: 6, maxBarThickness: 25 }],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { right: 35 } },
        plugins: { legend: { display: false } },
        scales: {
          x: { beginAtZero: true, ...(percentageScale ? { max: 100 } : {}), ticks: { precision: percentageScale ? 0 : 0, callback: valueSuffix ? (value: number | string) => `${value}${valueSuffix}` : undefined } },
          y: { grid: { display: false }, ticks: { font: { weight: '700' } } },
        },
      },
    };
  }, [color, id, labels, percentageScale, title, valueSuffix, values]);
  return <article className="card clinical-chart-card business-strategic-chart-card">
    <div className="business-dashboard-card-heading"><div><strong>{title}</strong><span>{description}</span></div></div>
    <div className="clinical-chart-body"><BusinessChartCanvas id={id} config={config} empty={!labels.length ? empty : undefined} /></div>
  </article>;
}

function formatBusinessTrendDate(value: string) {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

function BusinessRevenueByService({ revenue }: { revenue: BusinessDashboardResponse['revenueByService'] }) {
  const serviceCatalog = [
    { key: 'consultation', label: 'Consultation', color: '#14b8a6' },
    { key: 'endoscopy', label: 'Endoscopy', color: '#f59e0b' },
    { key: 'fet', label: 'FET', color: '#ec4899' },
    { key: 'freezing', label: 'Freezing', color: '#8b5cf6' },
    { key: 'iui', label: 'IUI', color: '#3b82f6' },
    { key: 'ivf', label: 'IVF', color: '#e91e63' },
    { key: 'ot', label: 'OT', color: '#f97316' },
    { key: 'pathLab', label: 'Path Lab', color: '#84cc16' },
    { key: 'scan', label: 'Scan', color: '#0ea5e9' },
    { key: 'tesa', label: 'TESA', color: '#6366f1' },
    { key: 'pharmacy', label: 'Pharmacy', color: '#22c55e' },
    { key: 'fmd', label: 'FMD', color: '#a855f7' },
    { key: 'other', label: 'Other / unmapped', color: '#94a3b8' },
  ] as const;
  const services = serviceCatalog.filter((service) => Math.abs(revenue?.[service.key] ?? 0) >= .01);
  const values = services.map((service) => (revenue?.[service.key] ?? 0) / 100_000);
  const valueLabels = {
    id: 'business-revenue-service-value-labels',
    afterDatasetsDraw: (chart: any) => {
      const { ctx, chartArea } = chart;
      ctx.save();
      ctx.fillStyle = '#334155';
      ctx.font = '800 11px Inter, system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      chart.getDatasetMeta(0).data.forEach((bar: { x: number; y: number }, index: number) => {
        const value = values[index];
        if (value <= 0) return;
        ctx.fillText(`₹ ${value.toLocaleString('en-IN', { maximumFractionDigits: 1 })} L`, bar.x, Math.max(chartArea.top + 12, bar.y - 7));
      });
      ctx.restore();
    },
  };
  const config = {
    type: 'bar',
    plugins: [valueLabels],
    data: {
      labels: services.map((service) => service.label),
      datasets: [{ label: 'Revenue', data: values, backgroundColor: services.map((service) => service.color), borderRadius: 6, maxBarThickness: 58 }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 20 } },
      plugins: { legend: { display: false }, tooltip: { callbacks: { label: (context: { parsed: { y: number } }) => `Revenue: ₹ ${context.parsed.y.toLocaleString('en-IN', { maximumFractionDigits: 1 })} L` } } },
      scales: {
        y: { beginAtZero: true, grace: '12%', ticks: { callback: (value: number | string) => `₹ ${Number(value).toLocaleString('en-IN')} L` } },
        x: { grid: { display: false }, ticks: { font: { weight: '700' }, maxRotation: 0, minRotation: 0 } },
      },
    },
  };
  return <article className="card clinical-chart-card business-revenue-service-card">
    <div className="business-dashboard-card-heading">
      <div><strong>Revenue by Service</strong><span>Governed revenue split by Looker CPT category and Medics source.</span></div>
      <span className="business-pending-badge">{moneyLakh(revenue?.total)}</span>
    </div>
    <div className="clinical-chart-body business-revenue-service-chart-scroll"><div className="business-revenue-service-chart-inner"><BusinessChartCanvas id="business-revenue-by-service-chart" config={config} empty={!revenue ? 'Revenue classification is temporarily unavailable.' : undefined} /></div></div>
  </article>;
}

function BusinessRevenueTrend({ data }: { data: BusinessDashboardResponse | null }) {
  const points = (data?.revenueTrend ?? []).map((point) => ({ ...point, value: point.value / 100_000 }));
  if (!points.length) return <BusinessPolicyPending message="No governed revenue records match the selected scope." />;
  const maximum = Math.max(1, ...points.map((point) => point.value));
  const ceiling = Math.max(100, Math.ceil(maximum / 100) * 100);
  const x = (index: number) => points.length <= 1 ? 310 : 58 + (index * (530 / (points.length - 1)));
  const plotBottom = 250;
  const y = (value: number) => plotBottom - (value / ceiling * 190);
  const actualPoints = points.filter((point) => point.kind === 'actual').map((point, index) => `${x(index)},${y(point.value)}`).join(' ');
  const lastActualIndex = points.map((point) => point.kind).lastIndexOf('actual');
  const landingIndex = points.findIndex((point) => point.kind === 'landing');
  const targetIndex = points.findIndex((point) => point.kind === 'target');
  const target = data?.kpis?.revenueTarget;
  const landing = data?.kpis?.landing;
  const gap = target != null && landing != null ? Math.max(0, target - landing) : null;
  const toDate = data?.selection?.to ? new Date(`${data.selection.to}T00:00:00Z`) : null;
  const daysRemaining = data?.selection?.period === 'CURRENT_MONTH' && toDate ? new Date(Date.UTC(toDate.getUTCFullYear(), toDate.getUTCMonth() + 1, 0)).getUTCDate() - toDate.getUTCDate() : null;
  return (
    <div className="business-revenue-layout business-revenue-content">
      <div className="business-revenue-chart-wrap">
        <svg className="business-revenue-chart" viewBox="0 0 650 312" preserveAspectRatio="none" role="img" aria-label="Historical, selected-period and forecast revenue trend">
          <defs>
            <linearGradient id="business-revenue-bars" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#8fb0f8" stopOpacity=".72" /><stop offset="1" stopColor="#dce7ff" stopOpacity=".82" /></linearGradient>
            <linearGradient id="business-revenue-mtd-bar" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#8b5cf6" stopOpacity=".78" /><stop offset="1" stopColor="#ede9fe" stopOpacity=".9" /></linearGradient>
          </defs>
          {[0, .25, .5, .75, 1].map((ratio) => {
            const tick = ceiling * ratio;
            const tickY = y(tick);
            return <g key={tick}><line className="business-revenue-gridline" x1="34" x2="630" y1={tickY} y2={tickY} /><text className="business-revenue-axis" x="4" y={tickY + 4}>{tick.toLocaleString('en-IN')}</text></g>;
          })}
          {points.map((point, index) => point.kind === 'actual' ? <rect key={`${point.label}-bar`} x={x(index) - 23} y={y(point.value)} width="46" height={plotBottom - y(point.value)} rx="5" fill={index === lastActualIndex ? 'url(#business-revenue-mtd-bar)' : 'url(#business-revenue-bars)'} /> : null)}
          <polyline className="business-revenue-actual-line" points={actualPoints} />
          {landingIndex >= 0 && lastActualIndex >= 0 ? <line className="business-revenue-landing-line" x1={x(lastActualIndex)} y1={y(points[lastActualIndex].value)} x2={x(landingIndex)} y2={y(points[landingIndex].value)} /> : null}
          {targetIndex >= 0 && landingIndex >= 0 ? <line className="business-revenue-target-line" x1={x(landingIndex)} y1={y(points[landingIndex].value)} x2={x(targetIndex)} y2={y(points[targetIndex].value)} /> : null}
          {points.map((point, index) => (
            <g key={`${point.label}-${point.kind}`}>
              <circle className={`business-revenue-dot ${point.kind}`} cx={x(index)} cy={y(point.value)} r="4.5" />
              <text className={`business-revenue-point-value ${point.kind}`} x={x(index)} y={Math.max(22, y(point.value) - 11)} textAnchor="middle">{point.value.toLocaleString('en-IN', { maximumFractionDigits: 1 })}</text>
              <text className="business-revenue-x-label" x={x(index)} y="281" textAnchor="middle">
                {revenueAxisLabel(point.label).map((line, lineIndex) => <tspan key={`${line}-${lineIndex}`} x={x(index)} dy={lineIndex ? 14 : 0}>{line}</tspan>)}
              </text>
            </g>
          ))}
        </svg>
      </div>
      <aside className="business-forecast-panel" aria-label="Selected revenue forecast">
        <h4>{data?.selection?.period === 'CURRENT_MONTH' && data.selection.to ? `${new Date(`${data.selection.to}T00:00:00Z`).toLocaleString('en-IN', { month: 'short', year: 'numeric', timeZone: 'UTC' })} Forecast` : 'Selected-period outlook'}</h4>
        <div><span>Target</span><strong>{moneyLakh(target)}</strong></div>
        <div><span>Landing</span><strong>{moneyLakh(landing)}</strong></div>
        <div className="gap"><span>Gap</span><strong>{moneyLakh(gap)}</strong></div>
        <p><span>Required daily run rate</span><strong>{gap != null && daysRemaining && daysRemaining > 0 ? moneyLakh(gap / daysRemaining) : '—'}</strong></p>
        <p><span>Days remaining</span><strong>{daysRemaining == null ? '—' : `${daysRemaining} Days`}</strong></p>
        <em>{target == null ? 'Actual-only period' : percentageText(data?.kpis?.targetAchievementPercent)}</em>
      </aside>
    </div>
  );
}

function revenueAxisLabel(label: string) {
  const quarter = label.match(/^(Q[1-4]\s*-?)\s*(.+)$/i);
  if (quarter) return [quarter[1].trim(), quarter[2].trim()];
  const month = label.match(/^([A-Za-z]{3})\s+(\d{4})$/);
  if (month) return [month[1], month[2]];
  return [label];
}

function BusinessPatientFunnel({ data }: { data: BusinessDashboardResponse | null }) {
  return (
    <div className="business-funnel-list">
      {funnelStages(data).map((stage) => (
        <div className={`business-funnel-row tone-${stage.tone}`} key={stage.label}>
          <span className="business-funnel-icon" aria-hidden="true">{stage.icon}</span>
          <strong>{stage.label}</strong>
          <span className="business-funnel-track"><i style={{ width: `${stage.width}%` }} /></span>
          <b>{stage.value}</b>
          <em>{stage.percent}</em>
        </div>
      ))}
    </div>
  );
}

function BusinessCentrePerformance({ rows, overallConversionPercent }: { rows: BusinessDashboardResponse['centres']; overallConversionPercent: number | null }) {
  const totals = rows.reduce((sum, row) => ({ revenue: sum.revenue + (row.revenue ?? 0), target: sum.target + (row.target ?? 0), landing: sum.landing + (row.landing ?? 0), opuBilled: sum.opuBilled + (row.opuBilled ?? 0) }), { revenue: 0, target: 0, landing: 0, opuBilled: 0 });
  return (
    <article className="card clinical-chart-card business-secondary-card business-centre-card">
      <div className="business-dashboard-card-heading">
        <div><strong>Centre Performance (MTD)</strong><span>Revenue, target, landing and IVF conversion by centre.</span></div>
        <span className="business-pending-badge">Governed</span>
      </div>
      <div className="business-centre-table-wrap">
        <table className="business-centre-table">
          <thead><tr><th>Centre</th><th className="numeric-right">Revenue</th><th className="numeric-center">Target</th><th className="numeric-right">Landing</th><th className="numeric-center">OPU Billed</th><th className="numeric-center">Conversion</th><th>Status</th></tr></thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.centre}>
              <td><LocationIcon />{row.centre}</td><td className="numeric-right">{moneyLakh(row.revenue)}</td><td className={`numeric-center ${(row.targetAchievementPercent ?? 0) >= 96 ? 'positive' : 'negative'}`}>{percentageText(row.targetAchievementPercent)}</td><td className="numeric-right">{moneyLakh(row.landing)}</td><td className="numeric-center">{numberText(row.opuBilled)}</td><td className="numeric-center">{percentageText(row.conversionPercent)}</td><td>—</td>
              </tr>
            ))}
          <tr className="total"><td>Total</td><td className="numeric-right">{moneyLakh(totals.revenue)}</td><td className="numeric-center">{percentageText(totals.target > 0 ? totals.landing / totals.target * 100 : null)}</td><td className="numeric-right">{moneyLakh(totals.landing)}</td><td className="numeric-center">{numberText(totals.opuBilled)}</td><td className="numeric-center">{percentageText(overallConversionPercent)}</td><td>—</td></tr>
          </tbody>
        </table>
      </div>
    </article>
  );
}

function BusinessOpportunityPipeline() {
  return (
    <article className="card clinical-chart-card business-secondary-card business-pipeline-card">
      <div className="business-dashboard-card-heading">
        <div><strong>Opportunity / Pipeline</strong><span>Past opportunity pipelines.</span></div>
        <span className="business-pending-badge">Definition pending</span>
      </div>
      <BusinessPolicyPending message="Cohort and monetary valuation rules will be connected after business approval." />
    </article>
  );
}

function BusinessChannelPerformance({ revenue }: { revenue: BusinessDashboardResponse['revenueByChannel'] }) {
  const b2b = revenue?.b2b ?? 0;
  const b2c = revenue?.b2c ?? 0;
  const btl = revenue?.btl ?? 0;
  const unclassified = revenue?.unclassified ?? 0;
  const mappedTotal = revenue?.mappedRevenue || 0;
  const share = (value: number) => mappedTotal > 0 ? value / mappedTotal * 100 : 0;
  const b2bEnd = share(b2b);
  const b2cEnd = b2bEnd + share(b2c);
  const btlEnd = b2cEnd + share(btl);
  const donutStyle = { background: `conic-gradient(#4f7ee8 0 ${b2bEnd}%, #e91e63 ${b2bEnd}% ${b2cEnd}%, #f59e0b ${b2cEnd}% ${btlEnd}%, #cbd5e1 ${btlEnd}% 100%)` } as CSSProperties;
  return (
    <article className="card clinical-chart-card business-secondary-card business-channel-card">
      <div className="business-dashboard-card-heading"><div><strong>Channel Matrix (MTD)</strong><span>Business channel performance mix.</span></div></div>
      {!revenue ? <BusinessPolicyPending message="Revenue channel mapping is temporarily unavailable." /> : <div className="business-channel-live">
        <div className="business-channel-donut" style={donutStyle}><span><strong>{percentageText(revenue.mappingPercent)}</strong><small>mapped</small></span></div>
        <div className="business-channel-live-list">
          <div className="b2b"><i /> <strong>B2B</strong><span>{moneyLakh(b2b)}</span><em>{percentageText(share(b2b))}</em></div>
          <div className="b2c"><i /> <strong>B2C</strong><span>{moneyLakh(b2c)}</span><em>{percentageText(share(b2c))}</em></div>
          <div className="btl"><i /> <strong>BTL</strong><span>{moneyLakh(btl)}</span><em>{percentageText(share(btl))}</em></div>
        </div>
        {Math.abs(unclassified) >= .01 ? <p>{moneyLakh(unclassified)} awaiting patient-channel mapping</p> : null}
      </div>}
    </article>
  );
}

function BusinessPatientExperience({ nps }: { nps: BusinessDashboardResponse['nps'] }) {
  const promoter = nps?.promoterPercent ?? 0;
  const passive = nps?.passivePercent ?? 0;
  const detractor = nps?.detractorPercent ?? 0;
  const donutStyle = { background: `conic-gradient(#46bd62 0 ${promoter}%, #cbd1dc ${promoter}% ${promoter + passive}%, #df2f83 ${promoter + passive}% 100%)` } as CSSProperties;
  return (
    <article className="card clinical-chart-card business-secondary-card business-experience-card">
      <div className="business-dashboard-card-heading">
        <div><strong>Patient NPS (MTD)</strong><span>NPS response mix across domains.</span></div>
        <span className="business-pending-badge">{numberText(nps?.responses)} responses</span>
      </div>
      <div className="business-experience-top">
        <div className="business-donut business-nps-donut" style={donutStyle}><span><small>NPS</small><strong>{numberText(nps?.score)}</strong></span></div>
        <div className="business-nps-legend"><div><i className="promoter" />Promoters <b>{percentageText(nps?.promoterPercent)}</b></div><div><i className="passive" />Passives <b>{percentageText(nps?.passivePercent)}</b></div><div><i className="detractor" />Detractors <b>{percentageText(nps?.detractorPercent)}</b></div></div>
      </div>
      <div className="business-experience-bottom">
        <div className="business-response-rate"><span>Response Rate</span><strong>{percentageText(nps?.captureRate)}</strong></div>
      </div>
      <div className="business-attention-pill">{numberText(nps?.feedbackTaken)} of {numberText(nps?.eligibleVisits)} eligible checked-out visits</div>
    </article>
  );
}

function BusinessDoctorPerformance({ rows }: { rows: BusinessDashboardResponse['doctors'] }) {
  return (
    <article className="card clinical-chart-card business-bottom-card business-doctor-card">
      <div className="business-dashboard-card-heading">
        <div><strong><i className="business-bottom-heading-icon"><PatientIcon /></i>Doctor Performance (Top 5)</strong><span>Governed patient, advice and IVF-cycle activity by doctor.</span></div>
        <span className="business-pending-badge">Selected period</span>
      </div>
      <div className="business-doctor-table-wrap">
        <table className="business-doctor-table">
          <colgroup><col /><col /><col /><col /><col /></colgroup>
          <thead><tr><th>Doctor</th><th>Patients</th><th>IVF Advice</th><th>IVF Cycles</th><th>Conversion</th></tr></thead>
          <tbody>{rows.map((row) => <tr key={row.doctor}><td><span className="business-doctor-avatar"><PatientIcon /></span>{row.doctor}</td><td>{numberText(row.totalPatients)}</td><td>{numberText(row.ivfAdvised)}</td><td>{numberText(row.ivfCycles)}</td><td><strong className={(row.conversionRate ?? 0) >= 50 ? 'good' : (row.conversionRate ?? 0) >= 35 ? 'watch' : 'risk'}>{percentageText(row.conversionRate)}</strong></td></tr>)}</tbody>
        </table>
      </div>
    </article>
  );
}

function BusinessGrowthLeaders({ rows }: { rows: BusinessDashboardResponse['growthLeaders'] }) {
  return (
    <article className="card clinical-chart-card business-bottom-card business-growth-card">
      <div className="business-dashboard-card-heading"><div><strong><i className="business-bottom-heading-icon growth"><TrophyIcon /></i>Growth Leaders (vs last month)</strong><span>Top month-on-month performers.</span></div></div>
      <div className="business-growth-list">
        {rows.map((item, index) => <div className="business-growth-row" key={item.centre}>
          <span className="business-growth-rank">{index + 1}</span>
          <span className="business-growth-icon">{index === 2 ? <PatientIcon /> : <BuildingIcon />}</span>
          <div className="business-growth-name"><strong>{item.centre}</strong><small>Growth leader</small></div>
        <div className="business-growth-metrics"><span><strong>{growthText(item.revenueGrowthPercent)}</strong><small>Revenue</small></span><span><strong>{growthText(item.freshOpuGrowthPercent)}</strong><small>OPU Billed</small></span></div>
        </div>)}
      </div>
    </article>
  );
}

function growthText(value: number | null | undefined) {
  return value == null
    ? '—'
    : `${value >= 0 ? '↑' : '↓'} ${Math.abs(value).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 1 })}%`;
}

function BusinessTargetGapContributors({ rows }: { rows: BusinessDashboardResponse['gapDrivers'] }) {
  return (
    <article className="card clinical-chart-card business-bottom-card business-target-gap-card">
      <div className="business-dashboard-card-heading">
        <div><strong><i className="business-bottom-heading-icon concern"><WarningIcon /></i>Gap Drivers</strong><span>Centres contributing most to shortfall.</span></div>
        <span className="business-pending-badge">Target-to-date run-rate</span>
      </div>
      <div className="business-target-gap-list">
        {rows.map((item, index) => {
          const shortfallPercent = item.shortfallPercent
            ?? (item.achievementPercent == null
              ? null
              : Math.max(0, Math.round((100 - item.achievementPercent) * 10) / 10));
          return <div className="business-target-gap-row" key={item.centre}>
            <span className="business-target-gap-rank">{index + 1}</span>
            <span className="business-target-gap-centre"><strong>{item.centre}</strong><small>Actual {moneyLakh(item.actualRevenue)} · Due {moneyLakh(item.targetToDate)}</small></span>
            <span className="business-target-gap-money"><strong>{moneyLakh(item.gapRupees)}</strong><small>Shortfall</small></span>
            <strong className="business-target-gap-achievement risk">{percentageText(shortfallPercent)}</strong>
          </div>;
        })}
      </div>
      <div className="business-target-gap-summary"><strong>At least 20% below target-to-date; ranked by revenue shortfall.</strong></div>
    </article>
  );
}

function BusinessManagementAttention() {
  return (
    <article className="card clinical-chart-card business-bottom-card business-management-card">
      <div className="business-dashboard-card-heading">
        <div><strong><i className="business-bottom-heading-icon management"><BellIcon /></i>Management Attention (Top 5)</strong><span>Priority actions from current business indicators.</span></div>
        <span className="business-pending-badge">Policy pending</span>
      </div>
      <BusinessPolicyPending message="Priority rules and severity thresholds will be connected after management approval." />
    </article>
  );
}

function BusinessKpiCard({ item }: { item: BusinessKpi }) {
  const accentStyle = { '--business-kpi-accent': KPI_TONES[item.tone] } as CSSProperties;
  return (
    <article className={`business-dashboard-kpi-card tone-${item.tone}${item.trend ? ' has-trend' : ''}`} style={accentStyle}>
      <div className="business-dashboard-kpi-head">
        <span className="business-dashboard-kpi-icon" aria-hidden="true">{item.icon}</span>
        <span>{item.label}</span>
      </div>
      <div className="business-dashboard-kpi-primary">
        <strong className="business-dashboard-kpi-value">{item.value}</strong>
        {item.trend && (
          <div className={`business-dashboard-kpi-trend ${item.trendDirection === 'down' ? 'down' : 'up'}`}>
            <span>{item.trendDirection === 'down' ? '\u2193' : '\u2197'} {item.trend}</span>
            <small>{item.comparison}</small>
          </div>
        )}
        {item.badge && <span className="business-dashboard-kpi-badge">{item.badge}</span>}
      </div>
      <div className="business-dashboard-kpi-footer">
        {item.detail && <div className="business-dashboard-kpi-detail">{item.detail}</div>}
        {item.note && <div className="business-dashboard-kpi-note">{item.note}</div>}
        {typeof item.progress === 'number' && (
          <div className="business-dashboard-kpi-progress">
            <span aria-hidden="true"><i style={{ width: `${Math.max(0, Math.min(100, item.progress))}%` }} /></span>
            <strong>{item.progressLabel}</strong>
          </div>
        )}
      </div>
    </article>
  );
}

type IconProps = { className?: string };

function IconBase({ children, className }: { children: ReactNode; className?: string }) {
  return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>;
}

function RupeeIcon(_: IconProps) { return <IconBase><path d="M6 4h12M6 8h12M7 4c6 0 7 8 0 8h-1l8 8" /></IconBase>; }
function LandingIcon(_: IconProps) { return <IconBase><path d="M5 20V11M10 20V7M15 20v-5M20 20V3" /><path d="m4 8 5-4 5 4 6-6" /></IconBase>; }
function TargetIcon(_: IconProps) { return <IconBase><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3" /><path d="m14 10 6-6M16 4h4v4" /></IconBase>; }
function PeopleIcon(_: IconProps) { return <IconBase><circle cx="9" cy="8" r="3" /><circle cx="17" cy="9" r="2.5" /><path d="M3 20c0-4 2.5-6 6-6s6 2 6 6M15 14c3 0 5 2 5 5" /></IconBase>; }
function PatientIcon(_: IconProps) { return <IconBase><circle cx="12" cy="7" r="4" /><path d="M5 21c0-5 2.8-8 7-8s7 3 7 8" /></IconBase>; }
function AdviceIcon(_: IconProps) { return <IconBase><path d="M4 5h16v12H9l-5 4z" /><path d="M8 9h8M8 13h5" /></IconBase>; }
function PercentIcon(_: IconProps) { return <IconBase><circle cx="7" cy="7" r="2.5" /><circle cx="17" cy="17" r="2.5" /><path d="M19 5 5 19" /></IconBase>; }
function StarIcon(_: IconProps) { return <IconBase><path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-2.9-5.6 2.9 1.1-6.2L3 9.6l6.2-.9z" /></IconBase>; }
function CheckIcon(_: IconProps) { return <IconBase><path d="m6 12 4 4 8-9" /></IconBase>; }
function ArrowRightIcon(_: IconProps) { return <IconBase><path d="M5 12h14M14 7l5 5-5 5" /></IconBase>; }
function FunnelIcon(_: IconProps) { return <IconBase><path d="M4 5h16l-6 7v6l-4 2v-8z" /></IconBase>; }
function HourglassIcon(_: IconProps) { return <IconBase><path d="M7 3h10M7 21h10M8 3c0 5 1 6 4 9-3 3-4 4-4 9M16 3c0 5-1 6-4 9 3 3 4 4 4 9" /></IconBase>; }
function BuildingIcon(_: IconProps) { return <IconBase><path d="M5 21V4h10v17M15 9h4v12M8 8h4M8 12h4M8 16h4" /></IconBase>; }
function LocationIcon(_: IconProps) { return <IconBase><path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" /><circle cx="12" cy="10" r="2.5" /></IconBase>; }
function TrophyIcon(_: IconProps) { return <IconBase><path d="M8 4h8v4c0 4-2 6-4 6s-4-2-4-6zM10 14v3M14 14v3M8 20h8M8 6H4v2c0 3 2 5 5 5M16 6h4v2c0 3-2 5-5 5" /></IconBase>; }
function WarningIcon(_: IconProps) { return <IconBase><path d="M12 3 2.5 20h19z" /><path d="M12 9v5M12 17h.01" /></IconBase>; }
function BellIcon(_: IconProps) { return <IconBase><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" /></IconBase>; }
